import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { AuthResult } from "./types";
import { detectBrowser } from "@/utils/browser";
import logger from "@/utils/logger";

interface UserMetadata {
  name?: string;
  full_name?: string;
  [key: string]: any;
}

export async function signIn(
  email: string,
  password: string
): Promise<AuthResult> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          status: error.status,
        },
      };
    }

    return {
      success: true,
      session: data.session,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.message || "An error occurred during sign in",
      },
    };
  }
}

export async function signUp(
  email: string,
  password: string,
  userData: UserMetadata
): Promise<AuthResult> {
  try {
    // Format user metadata
    const userMetadata: UserMetadata = {
      full_name: userData.name || "",
      ...userData,
    };
    
    // Check for iOS Safari and store pending price ID in database if needed
    const pendingPriceId = localStorage.getItem('pendingPriceId');
    const { name: browserName, isIOS } = detectBrowser();
    const isIOSSafari = isIOS && browserName === 'Safari';
    
    // Store pendingPriceId in database for iOS Safari users as a fallback
    if (isIOSSafari && pendingPriceId) {
      logger.info('Storing pendingPriceId in database for iOS Safari user', { email, pendingPriceId });
      
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/store-pending-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ email: email.toLowerCase(), pendingPriceId })
        });
        
        const result = await response.json();
        if (result.success) {
          logger.info('Successfully stored pendingPriceId in database', { email, pendingPriceId });
        } else {
          logger.error('Failed to store pendingPriceId in database', { error: result.error });
        }
      } catch (dbError) {
        // Log error but continue with signup - we'll still have localStorage as fallback
        logger.error('Error storing pendingPriceId in database', { error: dbError });
      }
    }

    // Construct the redirect URL for email verification to go to the verification handler
    // This dedicated handler will manage the session establishment and redirect to checkout if needed
    const verifyHandlerUrl = `${window.location.origin}/verify-handler`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userMetadata,
        emailRedirectTo: verifyHandlerUrl, // Redirect to our dedicated verification handler
      },
    });

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          status: error.status,
        },
      };
    }

    return {
      success: true,
      session: data.session,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.message || "An error occurred during sign up",
      },
    };
  }
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export async function refreshUserData(): Promise<User | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user || null;
}

/**
 * Updates user metadata in Supabase
 * @param metadata Object containing metadata key-value pairs to update
 * @returns Updated user data or null if operation failed
 */
export async function updateUserMetadata(metadata: Record<string, any>): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: metadata
    });
    
    if (error) {
      logger.error('Error updating user metadata:', error);
      return null;
    }
    
    return data.user;
  } catch (error) {
    logger.error('Exception updating user metadata:', error);
    return null;
  }
}

export async function changePassword(password: string): Promise<AuthResult> {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          status: error.status,
        },
      };
    }

    return {
      success: true,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.message || "An error occurred during password change",
      },
    };
  }
}
